import React from 'react';
import { motion } from 'framer-motion';
import { useCourse } from '../../context/CourseContext';
import { Progress } from '../ui/progress';
import {
  CheckCircle2,
  Circle,
  BookOpen,
  Award,
  Flame,
  Target,
} from 'lucide-react';

export const ProgressTracker = () => {
  const { chapters, getChapterProgress, overallProgress, journalEntries } = useCourse();

  const completedChapters = chapters.filter(ch => getChapterProgress(ch.id).completed).length;
  const startedChapters = chapters.filter(ch => getChapterProgress(ch.id).started).length;
  const journalCount = Object.keys(journalEntries).length;

  const stats = [
    {
      icon: BookOpen,
      label: 'Chapters Read',
      value: `${completedChapters}/${chapters.length}`,
      color: 'text-primary',
      bg: 'bg-primary/10',
      shadow: 'shadow-glow',
    },
    {
      icon: Flame,
      label: 'In Progress',
      value: startedChapters - completedChapters,
      color: 'text-secondary',
      bg: 'bg-secondary/10',
      shadow: 'shadow-gold',
    },
    {
      icon: Target,
      label: 'Journal Entries',
      value: journalCount,
      color: 'text-maroon',
      bg: 'bg-maroon/10',
      shadow: 'shadow-maroon',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Overall Progress */}
      <div className="bg-card border border-silver/20 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-foreground">
              Your Journey
            </h3>
            <p className="text-sm text-muted-foreground">
              Track your transformation progress
            </p>
          </div>
          <div className="text-right">
            <span className="text-3xl font-display font-bold text-maroon">
              {overallProgress}%
            </span>
            <p className="text-xs text-muted-foreground">Complete</p>
          </div>
        </div>

        <Progress value={overallProgress} className="h-3 mb-6" />

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className={`w-12 h-12 rounded-full ${stat.bg} ${stat.shadow} flex items-center justify-center mx-auto mb-2`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-xl font-semibold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Chapter Progress List */}
      <div className="bg-card border border-silver/20 rounded-xl p-6 shadow-sm">
        <h3 className="font-display text-lg font-semibold text-foreground mb-4">
          Chapter Progress
        </h3>

        <div className="space-y-3">
          {chapters.map((chapter, index) => {
            const progress = getChapterProgress(chapter.id);
            const hasJournal = journalEntries[chapter.id]?.content?.length > 0;

            return (
              <motion.div
                key={chapter.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                  progress.completed 
                    ? 'bg-success/5 border border-success/20' 
                    : progress.started 
                    ? 'bg-primary/5 border border-primary/20' 
                    : 'bg-silver/5 border border-silver/10'
                }`}
              >
                {/* Status Icon */}
                <div className={`flex-shrink-0 ${
                  progress.completed 
                    ? 'text-success' 
                    : progress.started 
                    ? 'text-primary' 
                    : 'text-silver'
                }`}>
                  {progress.completed ? (
                    <CheckCircle2 className="w-6 h-6" />
                  ) : (
                    <Circle className="w-6 h-6" />
                  )}
                </div>

                {/* Chapter Info */}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium truncate ${
                    progress.completed || progress.started 
                      ? 'text-foreground' 
                      : 'text-muted-foreground'
                  }`}>
                    {chapter.step}: {chapter.title}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span>{chapter.duration}</span>
                    {hasJournal && (
                      <span className="flex items-center gap-1 text-maroon">
                        <Award className="w-3 h-3" />
                        Journal written
                      </span>
                    )}
                  </div>
                </div>

                {/* Progress Indicator */}
                {progress.started && !progress.completed && (
                  <div className="flex-shrink-0 w-16">
                    <Progress value={progress.readingProgress || 0} className="h-1.5" />
                    <p className="text-xs text-muted-foreground text-right mt-1">
                      {progress.readingProgress || 0}%
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Completion Certificate Preview */}
      {overallProgress === 100 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-maroon/20 via-secondary/10 to-primary/10 border-2 border-maroon/30 rounded-xl p-8 text-center shadow-maroon"
        >
          <Award className="w-16 h-16 text-maroon mx-auto mb-4" />
          <h3 className="font-display text-2xl font-bold text-foreground mb-2">
            Congratulations!
          </h3>
          <p className="text-muted-foreground mb-4">
            You've completed "Taking Your Power Back" and taken a profound step 
            in your transformation journey.
          </p>
          <p className="text-sm text-maroon font-medium">
            You are the predominant creative force in your life.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default ProgressTracker;
